import { computed } from "vue";
import { useStore } from "@/core/services/store";

export const useChatBot = () => {
  const store = useStore();

  const messages = computed(() => store.getters["messages"]);
  const isLoading = computed(() => store.getters["isLoading"]);
  const isStreaming = computed(() => store.getters["isStreaming"]);
  const conversationId = computed(() => store.getters["conversationId"]);
  const error = computed(() => store.getters["error"]);

  /**
   * Initialize a new conversation with a greeting message
   */
  const initializeConversation = () => {
    return store.dispatch("initializeConversation");
  };

  /**
   * Add a message to the chat
   * @param {string} text - The message text
   * @param {string} sender - The sender of the message ('user' or 'bot')
   * @param {null} [messageId] - Optional message ID
   * @param isHtml
   * @returns {Object} The added message
   */
  const addMessage = (text, sender, messageId = null, isHtml = false) => {
    return store.dispatch("addMessage", {
      text,
      sender,
      messageId,
      isHtml
    });
  };

  /**
   * Updates the current streaming message with a new chunk of text
   * @param {string} chunk - The new text chunk
   * @param {boolean} aborted - Whether the streaming was aborted
   */
  const updateStreamingMessage = (chunk, aborted = false) => {
    return store.dispatch("updateStreamingMessage", { chunk, aborted });
  };

  /**
   * Sends a message to the chatbot API
   * @param {string} inputText - The input text from the user
   * @param {null} [abortController] - Optional ticket ID
   * @returns {Promise<void>}
   */
  const sendMessage = async (inputText, abortController = null) => {
    await store.dispatch("sendMessage", { inputText, abortController });
  };

  /**
   * Deletes all messages and resets the chat state
   */
  const clearMessages = () => {
    return store.dispatch("clearMessages");
  };

  const clearLoadingVariables = () => {
    return store.dispatch("clearLoadingVariables");
  };

  /**
   * Starts a new conversation by clearing messages and initializing
   */
  const startNewConversation = () => {
    return store.dispatch("startNewConversation");
  };

  // Initialisiere Konversation beim ersten Laden, wenn noch keine vorhanden ist
  if (!conversationId.value) {
    return initializeConversation();
  }

  return {
    messages,
    isLoading,
    isStreaming,
    conversationId,
    error,

    sendMessage,
    addMessage,
    clearMessages,
    clearLoadingVariables,
    updateStreamingMessage,
    startNewConversation,
    initializeConversation
  };
};
